import React, { useState, useEffect } from "react";
import { Box, IconButton, Alert, Tooltip, Button } from "@mui/material";
import { Share as ShareIcon, VideoLibrary as ReelIcon } from "@mui/icons-material";
import Cookies from 'js-cookie';
import FlutterServicesAPI from '../../services/flutterServices';
import { getBaseUrl } from '../../utils/environmentUtils';
import { imageCache } from './SecureImage';
import { videoCache } from './SecureVideo';

// Helper: Fetch blob from cache or network
// This reuses already-loaded images/videos to avoid duplicate API calls during share
const fetchFromCacheOrNetwork = async (url, isVideo, token) => {
  // Check if already cached by SecureImage/SecureVideo
  const cache = isVideo ? videoCache : imageCache;
  if (cache.has(url)) {
    console.log(`[ShareSocialButton] Using cached ${isVideo ? 'video' : 'image'} for:`, url);
    // Fetch the blob from the cached object URL
    const cachedObjectUrl = cache.get(url);
    const response = await fetch(cachedObjectUrl);
    return await response.blob();
  }

  // Not in cache, fetch from network with auth
  // Video-stream URLs redirect to a cross-origin S3 presigned URL which fails
  // CORS preflight for fetch(). ?direct=1 tells the backend to stream bytes
  // directly instead of redirecting, so fetch() stays same-origin.
  let fetchUrl = url;
  if (isVideo && url.includes('/video-stream/')) {
    fetchUrl = url + (url.includes('?') ? '&' : '?') + 'direct=1';
  }
  console.log(`[ShareSocialButton] Fetching from network:`, fetchUrl);
  const response = await fetch(fetchUrl, {
    method: 'GET',
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return await response.blob();
};

// ============================================================================
// SHARE SOCIAL BUTTON COMPONENT
// ============================================================================
// This component provides social media sharing functionality, specifically
// optimized for Instagram sharing (posts and reels)
// Can be used anywhere in the merchant side
//
// USAGE EXAMPLES:
// 
// Basic usage for images:
// <ShareSocialButton 
//   imageUrl="https://example.com/image.jpg" 
//   imageTitle="My Amazing Image"
//   onShareStatus={(status) => console.log(status)}
// />
//
// For videos:
// <ShareSocialButton 
//   imageUrl="https://example.com/video.mp4" 
//   imageTitle="My Amazing Video"
//   isVideo={true}
//   onShareStatus={(status) => console.log(status)}
// />
//
// Custom configuration:
// <ShareSocialButton 
//   imageUrl="https://example.com/image.jpg" 
//   imageTitle="My Image"
//   size="large"
//   orientation="horizontal"
//   showReels={false}
//   onShareStatus={(status) => handleShareStatus(status)}
// />
//
// PROPS:
// - imageUrl (string, required): URL of the image/video to share
// - imageTitle (string, optional): Title/description for the content
// - isVideo (boolean, optional): Whether the content is a video (default: false)
// - onShareStatus (function, optional): Callback for share status updates
// - showReels (boolean, optional): Show Instagram Reels button (default: true)
// - showPost (boolean, optional): Show Instagram Post button (default: true)
// - size (string, optional): Button size - "small", "medium", "large" (default: "medium")
// - orientation (string, optional): Button layout - "vertical", "horizontal" (default: "vertical")
// ============================================================================

const ShareSocialButton = ({
  imageUrl,
  imageTitle = "",
  shareTitle = "",
  shareText = "",
  isVideo = false,
  onShareStatus,
  showReels = true,
  showPost = true,
  size = "medium", // "small", "medium", "large"
  orientation = "vertical", // "vertical", "horizontal"
  category = null, // { display_name: "Necklace" }
  material = null, // { display_name: "Gold" }
  bustFinish = null, // { display_name: "SoftTouch" }
  otherAttributes = [], // Additional attributes to include in filename
  multiShare = false, // Enable multi-share mode
  selectedItems = [], // Array of selected item IDs for multi-share
  selectedItemsData = [], // Array of complete item objects for web multi-share
  getPreviewUrl = null, // Helper function to get preview URL from item
  getFileType = null, // Helper function to get file type from item
  variant = "icon", // "icon" or "button"
  label = "Share", // Label for button variant
  disabled = false, // Disable the button/icon
  sx = {}, // Custom styles for the root component
  hideIcon = false, // Option to hide the default icon
  className = "", // Additional CSS classes
  startIcon = null, // Custom start icon for button variant
  children = null, // Custom children for icon/button
}) => {
  const [shareStatus, setShareStatus] = useState(null);

  // Function to generate descriptive filename
  const generateDescriptiveFilename = () => {
    const parts = [];

    // Add category
    if (category?.display_name) {
      parts.push(category.display_name);
    }

    // Add material
    if (material?.display_name) {
      parts.push(material.display_name);
    }

    // Add bust finish
    if (bustFinish?.display_name) {
      parts.push(bustFinish.display_name);
    }

    // Add other attributes
    if (otherAttributes && otherAttributes.length > 0) {
      otherAttributes.forEach(attr => {
        if (attr?.display_name) {
          parts.push(attr.display_name);
        }
      });
    }

    // If no attributes available, fallback to default
    if (parts.length === 0) {
      return isVideo ? 'Generated Video' : 'Generated Image';
    }

    // Join parts with hyphens and clean up
    return parts.join('-').replace(/[^a-zA-Z0-9\-]/g, '').replace(/-+/g, '-');
  };

  // Generate final title and text for sharing
  const defaultTitle = generateDescriptiveFilename();

  // Glammify-branded default text
  const getGlammifyText = () => {
    const glammifyUrl = getBaseUrl();
    if (isVideo) {
      return `✨ Amazing AI-generated video created with Glammify! 🎥\n\nTransform your ideas into stunning visual content with our AI-powered platform. Perfect for social media, marketing, and creative projects! 🚀\n\nTry Glammify: ${glammifyUrl}\n\n#Glammify #AIGenerated #Creative #Video #AI #Innovation #DigitalArt #ContentCreation`;
    } else {
      return `✨ Stunning AI-generated image created with Glammify! 🎨\n\nBring your creative vision to life with our advanced AI technology. Perfect for social media, marketing, and artistic projects! 🚀\n\nTry Glammify: ${glammifyUrl}\n\n#Glammify #AIGenerated #Creative #Art #AI #Innovation #DigitalArt #ContentCreation`;
    }
  };

  const defaultText = getGlammifyText();
  const finalTitle = (shareTitle || imageTitle || defaultTitle || '').trim();

  // Ensure we never use an empty message. If shareText is empty or only whitespace, use defaultText.
  const finalText = (shareText && shareText.trim().length > 0) ? shareText : defaultText;

  useEffect(() => {
    // console.log('[ShareSocialButton] props', {
    //   imageUrl,
    //   imageTitle,
    //   shareTitle,
    //   shareText,
    //   isVideo,
    //   showReels,
    //   showPost,
    //   size,
    //   orientation,
    // });
  }, [imageUrl, imageTitle, shareTitle, shareText, isVideo, showReels, showPost, size, orientation]);

  // NOTE: Removed probe useEffect that was fetching imageUrl on mount.
  // This was causing N+1 API calls (20+ extra requests for 10 gallery items).
  // The actual fetch happens in handleShare() when user clicks to share.

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  // Ensure imageTitle is always a string, use blank if empty
  const safeImageTitle = imageTitle || "";

  const updateShareStatus = (status) => {
    setShareStatus(status);
    if (onShareStatus) {
      onShareStatus(status);
    }
  };

  /**
   * Unified helper to execute the Web Share API.
   * Tries multiple combinations of fields to maximize compatibility across different mobile browsers.
   */
  /**
   * Unified helper to execute the Web Share API.
   * Tries multiple combinations of fields to maximize compatibility across different mobile browsers.
   */
  const executeWebShare = async (title, text, files) => {
    if (!navigator.share) return false;

    // Hardcode the Glammify message directly here
    const glammifyUrl = getBaseUrl();
    const hasVideo = files && files.some(f => f.type && f.type.startsWith('video/'));

    const hardcodedText = hasVideo
      ? `✨ Amazing AI-generated video created with Glammify! 🎥\n\nTransform your ideas into stunning visual content with our AI-powered platform. Perfect for social media, marketing, and creative projects! 🚀\n\nTry Glammify: ${glammifyUrl}\n\n#Glammify #AIGenerated #Creative #Video #AI #Innovation #DigitalArt #ContentCreation`
      : `✨ Stunning AI-generated image created with Glammify! 🎨\n\nBring your creative vision to life with our advanced AI technology. Perfect for social media, marketing, and artistic projects! 🚀\n\nTry Glammify: ${glammifyUrl}\n\n#Glammify #AIGenerated #Creative #Art #AI #Innovation #DigitalArt #ContentCreation`;

    // Priority: use the hardcoded text to ensure it's always correct
    const finalTextToShare = hardcodedText;

    console.log('[ShareSocialButton] executeWebShare', {
      title: (title || '').substring(0, 20) + '...',
      textLength: finalTextToShare.length,
      filesCount: files?.length,
      hasVideo
    });

    // Detect Android to apply specific fallbacks
    const isAndroid = /Android/i.test(navigator.userAgent);
    const isSingleFile = files && files.length === 1;

    // AUTOMATIC CLIPBOARD COPY FOR ANDROID SINGLE SHARE
    // Cause: Android 'ACTION_SEND' (single file) often drops the 'text' (message) and only sends the file.
    // Fix: We automatically copy the message to the clipboard so the user can paste it.
    // This is a standard workaround for Instagram/TikTok/WhatsApp sharing flows.
    if (isAndroid && isSingleFile && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(finalTextToShare);
        console.log('[ShareSocialButton] Copied text to clipboard for Android single share');
        updateShareStatus({
          type: 'success',
          message: 'Caption copied! Paste it if it doesn\'t appear.'
        });
      } catch (clipError) {
        console.warn('[ShareSocialButton] Clipboard copy failed', clipError);
      }
    }

    try {
      // 1. Try text + files (Standard)
      // We prioritize this because it's the simplest valid payload for "Message + Media".
      const textFilesData = { text: finalTextToShare, files };
      if (navigator.canShare && navigator.canShare(textFilesData)) {
        console.log('[ShareSocialButton] Sharing text + files (preferred)');
        await navigator.share(textFilesData);
        return true;
      }

      // 2. Try title + text + files (Full payload)
      const fullData = { title, text: finalTextToShare, files };
      if (navigator.canShare && navigator.canShare(fullData)) {
        console.log('[ShareSocialButton] Sharing title + text + files');
        await navigator.share(fullData);
        return true;
      }

      // 3. Last resort: Try just files but include text anyway
      const filesOnlyData = { files };
      if (navigator.canShare && navigator.canShare(filesOnlyData)) {
        console.log('[ShareSocialButton] Sharing files + (trying text)');
        await navigator.share({ text: finalTextToShare, files });
        return true;
      }

      return false;
    } catch (error) {
      if (error.name === 'AbortError') {
        updateShareStatus({ type: 'info', message: 'Share cancelled' });
        return 'CANCELLED';
      }
      console.error('[ShareSocialButton] Web Share failed', error);
      return false;
    }
  };

  // ============================================================================
  // INSTAGRAM POST SHARING FUNCTION
  // ============================================================================
  // This function handles sharing to Instagram as a regular post
  // It tries multiple methods: file sharing, URL sharing, then fallback
  // ============================================================================

  const handleShare = async () => {
    console.log('[ShareSocialButton] handleShare start', { imageUrl, isVideo });

    updateShareStatus(null);

    const mediaType = isVideo ? 'video' : 'image';
    const fileExtension = isVideo ? 'mp4' : 'jpg';
    const mimeType = isVideo ? 'video/mp4' : 'image/jpeg';

    // Check for mixed content types (video + image)
    let effectiveMimeType = mimeType;
    if (multiShare && selectedItemsData && selectedItemsData.length > 0 && getFileType) {
      const hasVideo = selectedItemsData.some(item => getFileType(item) === 'video');
      const hasImage = selectedItemsData.some(item => getFileType(item) !== 'video');

      if (hasVideo && hasImage) {
        effectiveMimeType = '*/*';
        console.log('[ShareSocialButton] Mixed content detected, using */* mime type');
      }
    }

    // --------------------------------------------------------------------------
    // Flutter integration: if inside Flutter app, trigger native share via bridge
    // This calls the 'socialShare' handler with a payload containing media info
    // Always keep this as the earliest branch so web falls back only if needed
    // --------------------------------------------------------------------------
    try {
      if (FlutterServicesAPI.isFlutterApp()) {
        // Build attributes list for better context in native share payload
        const attributes = [];
        if (category?.display_name) attributes.push(category.display_name);
        if (material?.display_name) attributes.push(material.display_name);
        if (bustFinish?.display_name) attributes.push(bustFinish.display_name);
        if (Array.isArray(otherAttributes)) {
          for (const attr of otherAttributes) {
            if (attr?.display_name) attributes.push(attr.display_name);
          }
        }

        const shareData = {
          // Core fields used by Flutter for native share (single item or first item of selection)
          accessToken: Cookies.get('merchant_access_token'),
          title: finalTitle,
          shareMessage: finalText, // includes the Glammify-branded message
          text: finalText, // Alias for compatibility
          message: finalText, // Alias for compatibility
          shareUrl: getBaseUrl(),
          url: imageUrl, // direct media URL (image or video stream)
          isVideo: !!isVideo,
          fileName: `${defaultTitle}.${fileExtension}`,
          mimeType: effectiveMimeType,
          // Explicit lists for Android Intent headers (EXTRA_MIME_TYPES) if supported by bridge
          mimeTypes: (multiShare && Array.isArray(selectedItemsData) && getFileType)
            ? selectedItemsData.map(item => getFileType(item) === 'video' ? 'video/mp4' : 'image/jpeg')
            : [mimeType],
          fileNames: (multiShare && Array.isArray(selectedItemsData) && getFileType)
            ? selectedItemsData.map(item => `glammify_${item.id}.${getFileType(item) === 'video' ? 'mp4' : 'jpg'}`)
            : [`${defaultTitle}.${fileExtension}`],
          // Optional metadata for Flutter-side UX
          attributes,
          // Multi-share support
          multiShare: !!multiShare,
          selectedItems: multiShare && Array.isArray(selectedItems) ? selectedItems : [], // Pass selected item IDs
          // Detailed per-file data for Flutter to handle multiple downloads/shares
          selectedFiles: (multiShare && Array.isArray(selectedItemsData) && getPreviewUrl && getFileType)
            ? selectedItemsData.map(item => {
              const itemType = getFileType(item);
              const itemIsVideo = itemType === 'video';
              const ext = itemIsVideo ? 'mp4' : 'jpg';
              return {
                id: item.id,
                url: getPreviewUrl(item),
                isVideo: itemIsVideo,
                fileName: `glammify_${item.id}.${ext}`,
                mimeType: itemIsVideo ? 'video/mp4' : 'image/jpeg'
              };
            })
            : [],
        };

        console.log('[ShareSocialButton] Calling Flutter socialShare', {
          multiShare: shareData.multiShare,
          selectedItemsCount: shareData.selectedItems.length,
          shareData
        });

        console.log('[ShareSocialButton] Flutter Payload Message:', shareData.shareMessage);

        // Call Flutter bridge handler for native social sharing
        const flutterResult = await window.flutter_inappwebview.callHandler('socialShare', shareData);

        // If Flutter returns a structured success, surface it; otherwise assume success
        if (flutterResult && flutterResult.success === false) {
          console.error('[ShareSocialButton] Flutter share failed:', flutterResult);
          updateShareStatus({ type: 'error', message: flutterResult.message || 'Share failed on mobile' });
        } else {
          // Success message suppressed as per request
          console.log(`Share initiated via mobile app`);
        }
        return; // Prevent web flow when handled by Flutter
      }
    } catch (flutterError) {
      console.warn('[ShareSocialButton] Flutter socialShare failed, falling back to web flow', flutterError);
      // Intentionally continue to web flow below
    }



    if (FlutterServicesAPI.isFlutterApp()) {
      return;
    }

    // ============================================================================
    // WEB MULTI-SHARE LOGIC
    // ============================================================================
    // Handle multi-share for web browsers - download and share files
    // This flow works better on mobile browsers (Android Chrome, iOS Safari)
    // for sharing message + images together
    // ============================================================================
    if (multiShare && selectedItemsData && selectedItemsData.length > 0 && getPreviewUrl && getFileType) {
      console.log('[ShareSocialButton] Web multi-share flow', { itemCount: selectedItemsData.length });

      try {
        updateShareStatus({ type: 'info', message: `Preparing ${selectedItemsData.length} items for sharing...` });

        // Get authentication token
        const isAdminPortal = window.location.pathname.startsWith('/glam-admin/');
        const token = isAdminPortal
          ? (localStorage.getItem('access_token') || Cookies.get('access_token'))
          : Cookies.get('merchant_access_token');

        if (!token) {
          throw new Error('No authentication token found');
        }

        // Download all selected items in parallel (use cache if available)
        let processedCount = 0;
        const totalCount = selectedItemsData.length;

        const downloadTasks = selectedItemsData.map(async (item) => {
          try {
            const itemUrl = getPreviewUrl(item);
            const itemType = getFileType(item);
            const itemIsVideo = itemType === 'video';
            const fileExtension = itemIsVideo ? 'mp4' : 'jpg';
            const mimeType = itemIsVideo ? 'video/mp4' : 'image/jpeg';

            // Use cache-aware fetch to avoid duplicate network requests
            const blob = await fetchFromCacheOrNetwork(itemUrl, itemIsVideo, token);
            const file = new File([blob], `glammify_${item.id}.${fileExtension}`, { type: mimeType });

            processedCount++;
            updateShareStatus({
              type: 'info',
              message: `Preparing items (${processedCount}/${totalCount})...`
            });

            console.log(`[ShareSocialButton] Got item ${item.id} (${file.size} bytes) - ${processedCount}/${totalCount}`);
            return file;
          } catch (itemError) {
            console.error(`Error getting item ${item.id}:`, itemError);
            processedCount++; // Still increment count to keep progress moving
            return null;
          }
        });

        const results = await Promise.all(downloadTasks);
        const files = results.filter(f => f !== null);

        if (files.length === 0) {
          throw new Error('Failed to download any files');
        }

        console.log(`[ShareSocialButton] Downloaded ${files.length} files for sharing`);

        // Try Web Share API with multiple files using unified helper
        const shared = await executeWebShare(
          finalTitle || 'Glammify Media',
          finalText,
          files
        );

        if (shared === true) {
          updateShareStatus({ type: 'success', message: `Successfully shared ${files.length} item${files.length > 1 ? 's' : ''}!` });
          return;
        } else if (shared === 'CANCELLED') {
          return;
        }

        // Fallback: Download all files
        updateShareStatus({ type: 'info', message: `Downloading ${files.length} files...` });
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const downloadUrl = URL.createObjectURL(file);
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = file.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(downloadUrl);

          // Small delay between downloads to prevent browser blocking
          if (i < files.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        }

        updateShareStatus({
          type: 'info',
          message: `Downloaded ${files.length} files! You can now upload them to Instagram or other social media platforms.`
        });
        return;

      } catch (error) {
        console.error('[ShareSocialButton] Multi-share failed:', error);
        updateShareStatus({
          type: 'error',
          message: `Failed to share ${selectedItemsData.length} items: ${error.message}`
        });
        return;
      }
    }

    // For videos, download the file and share it
    if (isVideo) {
      console.log('[ShareSocialButton] video flow: downloading and sharing file');

      try {
        // Download the video file (use cache if available)
        updateShareStatus({ type: 'info', message: 'Preparing video for sharing...' });

        // Get the correct authentication token based on context
        const isAdminPortal = window.location.pathname.startsWith('/glam-admin/');
        const token = isAdminPortal
          ? (localStorage.getItem('access_token') || Cookies.get('access_token'))
          : Cookies.get('merchant_access_token');

        if (!token) {
          throw new Error('No authentication token found');
        }

        // Use cache-aware fetch to avoid duplicate network requests
        const videoBlob = await fetchFromCacheOrNetwork(imageUrl, true, token);
        console.log('[ShareSocialButton] video ready', { size: videoBlob.size, type: videoBlob.type });

        // Create a file from the blob using the actual type if available
        const actualMimeType = videoBlob.type || mimeType || 'video/mp4';
        const videoFile = new File([videoBlob], `${defaultTitle}.mp4`, { type: actualMimeType });

        // Try to share the video file using unified helper
        const shared = await executeWebShare(
          finalTitle || 'Glammify Video',
          finalText,
          [videoFile]
        );

        if (shared === true) {
          updateShareStatus({ type: 'success', message: 'Video shared successfully!' });
          return;
        } else if (shared === 'CANCELLED') {
          return;
        }

        // Fallback: Download the video file for manual sharing
        const downloadUrl = URL.createObjectURL(videoBlob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `${defaultTitle}.mp4`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);

        updateShareStatus({
          type: 'info',
          message: 'Video downloaded! You can now upload it to Instagram or other social media platforms.'
        });
        return;

      } catch (error) {
        console.error('[ShareSocialButton] video download/share failed', error);
        updateShareStatus({
          type: 'error',
          message: `Failed to share video: ${error.message}. Please try downloading manually.`
        });
        return;
      }
    }

    // For images, try direct file sharing first
    try {
      console.log('[ShareSocialButton] image flow: using cache if available', { imageUrl });

      // Get the correct authentication token based on context
      const isAdminPortal = window.location.pathname.startsWith('/glam-admin/');
      const token = isAdminPortal
        ? (localStorage.getItem('access_token') || Cookies.get('access_token'))
        : Cookies.get('merchant_access_token');

      // Use cache-aware fetch to avoid duplicate network requests
      const blob = await fetchFromCacheOrNetwork(imageUrl, false, token);
      console.log('[ShareSocialButton] blob info', { size: blob.size, type: blob.type });

      // Check file size (must be at least 1KB for Instagram)
      if (blob.size < 1024) {
        throw new Error('File too small (less than 1KB)');
      }

      const actualMimeType = blob.type || mimeType || 'image/jpeg';
      const file = new File([blob], `${defaultTitle.toLowerCase().replace(/\s+/g, '-')}.${fileExtension}`, {
        type: actualMimeType
      });
      console.log('[ShareSocialButton] file created', { name: file.name, size: file.size, type: file.type });

      // Try Web Share API with image file using unified helper
      const shared = await executeWebShare(
        finalTitle || 'Glammify Image',
        finalText,
        [file]
      );

      if (shared === true) {
        updateShareStatus({ type: 'success', message: `${mediaType.charAt(0).toUpperCase() + mediaType.slice(1)} shared!` });
        return;
      } else if (shared === 'CANCELLED') {
        return;
      }
    } catch (fileError) {
      console.log('[ShareSocialButton] image file share path failed', fileError);
    }

    // Fallback: Download the image file for manual sharing
    try {
      updateShareStatus({ type: 'info', message: 'Preparing image for download...' });

      // Get the correct authentication token based on context
      const isAdminPortal = window.location.pathname.startsWith('/glam-admin/');
      const token = isAdminPortal
        ? (localStorage.getItem('access_token') || Cookies.get('access_token'))
        : Cookies.get('merchant_access_token');

      if (!token) {
        throw new Error('No authentication token found');
      }

      // Use cache-aware fetch to avoid duplicate network requests
      const imageBlob = await fetchFromCacheOrNetwork(imageUrl, false, token);
      console.log('[ShareSocialButton] image ready', { size: imageBlob.size, type: imageBlob.type });

      // Download the image file for manual sharing
      const downloadUrl = URL.createObjectURL(imageBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${defaultTitle}.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);

      updateShareStatus({
        type: 'info',
        message: 'Image downloaded! You can now upload it to Instagram or other social media platforms.'
      });
    } catch (downloadError) {
      console.error('[ShareSocialButton] image download failed', downloadError);
      updateShareStatus({
        type: 'error',
        message: `Failed to download image: ${downloadError.message}. Please try again.`
      });
    }
  };

  // ============================================================================
  // INSTAGRAM REELS SHARING FUNCTION
  // ============================================================================
  // This function handles sharing to Instagram as a Reel
  // It includes Reels-specific formatting and hashtags
  // ============================================================================

  const handleShareAsReel = async () => {
    updateShareStatus(null);

    const mediaType = isVideo ? 'video' : 'image';
    const fileExtension = isVideo ? 'mp4' : 'jpg';
    const mimeType = isVideo ? 'video/mp4' : 'image/jpeg';
    const emoji = isVideo ? '🎥' : '🎨';

    // Create Glammify-branded Reels content
    const reelText = finalText ? `${emoji} ${finalText}` : `${emoji} Amazing AI-generated ${mediaType} created with Glammify! ✨\n\nTransform your ideas into stunning visual content with our AI-powered platform! 🚀\n\n${getReelHashtags('')}\n\n#Glammify #AIGenerated #Reels #Instagram #Viral #Trending #Creative #AI #Innovation #DigitalArt #ContentCreation`;

    // First, try to download and share the media file directly
    try {
      updateShareStatus({ type: 'info', message: `Preparing ${mediaType} for Reel sharing...` });

      // Get the correct authentication token based on context
      const isAdminPortal = window.location.pathname.startsWith('/glam-admin/');
      const token = isAdminPortal
        ? (localStorage.getItem('access_token') || Cookies.get('access_token'))
        : Cookies.get('merchant_access_token');

      if (!token) {
        throw new Error('No authentication token found');
      }

      // Use cache-aware fetch to avoid duplicate network requests
      const blob = await fetchFromCacheOrNetwork(imageUrl, isVideo, token);
      const file = new File([blob], `${defaultTitle.toLowerCase().replace(/\s+/g, '-')}-reel.${fileExtension}`, {
        type: mimeType
      });

      // Try Web Share API with file
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file], text: reelText, title: finalTitle })) {
        await navigator.share({
          files: [file],
          title: finalTitle || 'Glammify Reel',
          text: reelText,
        });

        updateShareStatus({ type: 'success', message: `Reel ${mediaType} shared! Select Instagram from the share options to create your Reel directly.` });
        return;
      }
    } catch (fileError) {
      console.log("Reel file sharing failed, downloading for manual sharing:", fileError);

      // Fallback: Download the media file for manual sharing
      try {
        updateShareStatus({ type: 'info', message: `Preparing ${mediaType} for Reel download...` });

        // Get the correct authentication token based on context
        const isAdminPortal = window.location.pathname.startsWith('/glam-admin/');
        const token = isAdminPortal
          ? (localStorage.getItem('access_token') || Cookies.get('access_token'))
          : Cookies.get('merchant_access_token');

        if (!token) {
          throw new Error('No authentication token found');
        }

        // Use cache-aware fetch to avoid duplicate network requests
        const blob = await fetchFromCacheOrNetwork(imageUrl, isVideo, token);
        const file = new File([blob], `${defaultTitle.toLowerCase().replace(/\s+/g, '-')}-reel.${fileExtension}`, {
          type: mimeType
        });

        // Download the file for manual sharing
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `${defaultTitle.toLowerCase().replace(/\s+/g, '-')}-reel.${fileExtension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);

        updateShareStatus({
          type: 'info',
          message: `${mediaType.charAt(0).toUpperCase() + mediaType.slice(1)} downloaded for Reel! You can now upload it to Instagram Reels.`
        });
      } catch (downloadError) {
        console.error('[ShareSocialButton] reel download failed', downloadError);
        updateShareStatus({
          type: 'error',
          message: `Failed to download ${mediaType} for Reel: ${downloadError.message}. Please try again.`
        });
      }
    }
  };

  // ============================================================================
  // HASHTAG GENERATION FUNCTION
  // ============================================================================
  // This function generates context-aware hashtags for Instagram Reels
  // based on the image title/content
  // ============================================================================

  const getReelHashtags = (title) => {
    // Glammify-specific hashtags
    const glammifyHashtags = '#Glammify #AIGenerated #Creative #AI #Innovation #DigitalArt #ContentCreation #Tech #Future #ArtificialIntelligence';

    const hashtagMap = {
      'mountain': '#Mountains #Nature #Landscape #Adventure #Hiking #Outdoor #Scenic #Peak #Wilderness #Explore',
      'ocean': '#Ocean #Sea #Waves #Beach #Sunset #Nature #Water #Coastal #Serene #Blue',
      'forest': '#Forest #Trees #Nature #Woods #Green #Wilderness #Hiking #Trail #Peaceful #NatureLovers',
      'city': '#City #Urban #Skyline #Architecture #Modern #Night #Lights #Metropolitan #Downtown #CityLife',
      'desert': '#Desert #Sand #Dunes #Arid #Landscape #Golden #Vast #Hot #Adventure #Wilderness',
      'portrait': '#Portrait #Photography #Art #Creative #Beautiful #Amazing #Inspiring #Stunning #Wonderful #Perfect',
      'landscape': '#Landscape #Nature #Photography #Art #Creative #Beautiful #Amazing #Inspiring #Stunning #Wonderful',
      'abstract': '#Abstract #Art #Creative #Design #Modern #Contemporary #Beautiful #Amazing #Inspiring #Stunning'
    };

    const lowerTitle = title.toLowerCase();
    for (const [key, hashtags] of Object.entries(hashtagMap)) {
      if (lowerTitle.includes(key)) {
        return `${glammifyHashtags} ${hashtags}`;
      }
    }
    return `${glammifyHashtags} #Nature #Beautiful #Amazing #Photography #Art #Creative #Inspiring #Stunning #Wonderful #Perfect`;
  };

  // ============================================================================
  // SIZE CONFIGURATION
  // ============================================================================

  const getSizeConfig = () => {
    switch (size) {
      case 'small':
        return {
          buttonHeight: '36px',
          fontSize: '0.75rem',
          padding: '0.5rem 1rem',
          iconSize: 18
        };
      case 'large':
        return {
          buttonHeight: '56px',
          fontSize: '1rem',
          padding: '1rem 2rem',
          iconSize: 24
        };
      default: // medium
        return {
          buttonHeight: '48px',
          fontSize: '0.875rem',
          padding: '0.75rem 1.5rem',
          iconSize: 20
        };
    }
  };

  const sizeConfig = getSizeConfig();

  // ============================================================================
  // COMPONENT RENDER
  // ============================================================================
  // This section renders the UI with Instagram Post and Reel sharing buttons
  // ============================================================================

  return (
    <Box sx={variant === "button" ? { width: '100%', ...sx } : sx}>
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        {variant === "button" ? (
          <Button
            onClick={handleShare}
            variant="outlined"
            disabled={disabled}
            className={className}
            startIcon={startIcon ? startIcon : (hideIcon ? null : <ShareIcon />)}
            sx={{
              width: '100%',
              height: '48px',
              borderRadius: '24px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              borderColor: 'divider',
              color: 'text.primary',
              '&:hover': {
                bgcolor: 'action.hover',
                borderColor: 'text.primary',
              },
              ...sx,
            }}
          >
            {children ? children : label}
          </Button>
        ) : (
          <IconButton
            onClick={handleShare}
            disabled={disabled}
            size={size === 'small' ? 'small' : 'medium'}
            sx={{
              bgcolor: 'white',
              color: 'black',
              '&:hover': {
                bgcolor: '#f3f4f6',
              },
              width: 35,
              height: 35,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              ...sx,
            }}
          >
            {children ? children : <ShareIcon sx={{ fontSize: 20 }} />}
          </IconButton>
        )}
      </Box>

      {/* {shareStatus && (
        <Alert 
          severity={shareStatus.type} 
          sx={{ mt: 2, borderRadius: '10px', fontSize: '0.875rem' }}
        >
          {shareStatus.message}
        </Alert>
      )} */}

    </Box>
  );
};

export default ShareSocialButton;
